<template>
  <div class="dashboard-container">
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="mb-2 page-title">
      <i class="bi bi-telephone"></i> Narahubung
    </div>

    <div class="mb-3 d-flex flex-wrap gap-2 justify-content-between align-items-start">
      <DataToolbar
        v-model="searchQuery"
        placeholder="Cari nama narahubung atau turnamen..."
        :mencari="loading"
        @search="cari"
      >
        <template #filters>
          <!--
            Turnamen yang sudah selesai disembunyikan secara bawaan.
            Datanya tidak dihapus - hanya tidak ikut ditampilkan, supaya
            daftar tidak dipenuhi acara tahun-tahun sebelumnya.
          -->
          <label class="saring-selesai">
            <input type="checkbox" v-model="termasukSelesai" @change="cari(searchQuery)" />
            Tampilkan turnamen selesai
          </label>
        </template>
      </DataToolbar>

      <router-link to="/admin/contact-person/create" class="button button-primary">
        <i class="bi bi-plus-square"></i> Tambah
      </router-link>
    </div>

    <!-- Modal Konfirmasi Hapus -->
    <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Konfirmasi Hapus</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            Apakah Anda yakin ingin menghapus narahubung <strong>{{ selectedContactName }}</strong>?
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Batal</button>
            <button type="button" class="btn btn-danger" :disabled="deleting" @click="deleteContactPerson">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
              Ya, Hapus
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive mt-3">
      <table class="table mb-0">
        <thead>
          <tr>
            <th>No</th>
            <th>Turnamen</th>
            <th>Keterangan</th>
            <th>Nama</th>
            <th>Telepon</th>
            <th>Email</th>
            <th>Aksi</th>
          </tr>
        </thead>

        <tbody v-if="contactPersons.length > 0">
          <tr
            v-for="(contactPerson, index) in contactPersons"
            :key="contactPerson.id"
          >
            <td>{{ index + 1 + (currentPage - 1) * perPage }}</td>
            <td class="kolom-turnamen">{{ contactPerson.tournament?.name ?? '-' }}</td>
            <td>{{ contactPerson.description ?? '-' }}</td>
            <td>{{ contactPerson.name ?? '-' }}</td>
            <td>{{ contactPerson.phone ?? '-' }}</td>
            <td>{{ contactPerson.email ?? '-' }}</td>

            <td class="action-column">
              <div class="d-flex gap-2 justify-content-end">
                <MenuAksi v-slot="{ tutup }">
                  <button type="button" class="aksi__item"
                          @click="tutup(); editContactPerson(contactPerson.id)">
                    <i class="bi bi-pencil-square"></i> Ubah
                  </button>
                  <button type="button" class="aksi__item aksi__item--bahaya"
                          @click="tutup(); confirmDelete(contactPerson)">
                    <i class="bi bi-trash"></i> Hapus
                  </button>
                </MenuAksi>
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr>
            <td colspan="7" class="text-center">No contact person found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-end mt-4">
      <AppPagination
        :current-page="currentPage"
        :total-pages="totalPages"
        :prev-page-url="prevPageUrl"
        :next-page-url="nextPageUrl"
        @change-page="changePage"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/config/api";
import DataToolbar from "@/components/ui/DataToolbar.vue";
import MenuAksi from "@/components/ui/MenuAksi.vue";
import AppPagination from "@/components/ui/AppPagination.vue";
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";

axios.defaults.baseURL = API.API_BASE_URL;

export default {
  name: "ContactPersonList",

  components: { DataToolbar, MenuAksi, AppPagination },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      contactPersons: [],
      searchQuery: "",
      termasukSelesai: false,
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      prevPageUrl: null,
      nextPageUrl: null,
      loading: false,
      progress: 0,
      selectedContactId: null,
      selectedContactName: "",
      deleteModal: null,
      deleting: false,
    };
  },

  mounted() {
    this.loadContactPersons();
    const modalEl = document.getElementById("confirmDeleteModal");
    if (modalEl) {
      this.deleteModal = new Modal(modalEl);
    }
  },

  methods: {
    /** Dipanggil DataToolbar setelah pengguna berhenti mengetik (350 ms). */
    cari(kata) {
      this.searchQuery = kata ?? '';
      this.currentPage = 1;
      this.loadContactPersons(1);
    },

    async loadContactPersons(page = 1) {
      this.loading = true;

      try {
        const response = await axios.get("/tournament-contact-persons", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          params: {
            page,
            perPage: this.perPage,
            search: this.searchQuery.trim(),
            termasuk_selesai: this.termasukSelesai ? 1 : 0,
          },
        });

        const {
          current_page,
          last_page,
          data,
          next_page_url,
          prev_page_url,
        } = response.data;

        this.contactPersons = data || [];
        this.currentPage = current_page || page;
        this.totalPages = last_page || 0;
        this.nextPageUrl = next_page_url || null;
        this.prevPageUrl = prev_page_url || null;
      } catch (error) {
        console.error("Error loading contact persons:", error);

        this.contactPersons = [];
        this.currentPage = 1;
        this.totalPages = 0;
        this.nextPageUrl = null;
        this.prevPageUrl = null;
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (this.loading) {
        return;
      }

      if (page < 1 || page > this.totalPages) {
        return;
      }

      if (page === this.currentPage) {
        return;
      }

      this.loadContactPersons(page);
    },

    searchClasses() {
      this.currentPage = 1;
      this.loadContactPersons(1);
    },

    editContactPerson(id) {
      this.$router.push({
        name: "EditContactPerson",
        params: { id },
      });
    },

    confirmDelete(contact) {
      this.selectedContactId = contact.id;
      this.selectedContactName = contact.name || "Narahubung";
      if (this.deleteModal) {
        this.deleteModal.show();
      }
    },

    async deleteContactPerson() {
      if (!this.selectedContactId) return;
      this.deleting = true;
      try {
        const response = await axios.delete(`/tournament-contact-persons/${this.selectedContactId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });
        if (this.deleteModal) this.deleteModal.hide();
        this.toast.success(response.data?.message || "Narahubung berhasil dihapus.");
        this.loadContactPersons(this.currentPage);
      } catch (error) {
        if (this.deleteModal) this.deleteModal.hide();
        const pesan =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Gagal menghapus narahubung.";
        this.toast.error(pesan);
        console.error("Gagal menghapus contact person:", error);
      } finally {
        this.deleting = false;
      }
    },
  },

  computed: {
    prevPageExists() {
      return this.currentPage > 1;
    },

    nextPageExists() {
      return this.currentPage < this.totalPages;
    },
  },
};
</script>

<style scoped>
.saring-selesai {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  cursor: pointer;
}

.saring-selesai input { width: 16px; height: 16px; accent-color: var(--accent); }


.table th,
.table td {
  padding: 10px;
}




.form-control {
  width: 250px;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background-color: #388e3c;
  animation: loader-animation 1.5s infinite;
}

@keyframes loader-animation {
  0% {
    width: 0;
    background-color: #388e3c;
  }

  50% {
    width: 50%;
    background-color: #388e3c;
  }

  100% {
    width: 100%;
    background-color: #388e3c;
  }
}
</style>